import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'

import AddDoctorTable, { DoctorData } from '../AddDoctorTable'
import AddDoctorFormDialog, { AddDoctorFormValues } from '../AddDoctorFormDialog'

export type AddDoctorDialogProps = {
  doctors: DoctorData[]
  isAdding?: boolean
  isSearching?: boolean
  onAddDoctor?: (doctor: AddDoctorFormValues) => void
  onClose?: () => void
  onItemsPerPageChange?: (itemsPerPage: number) => void
  onPageChange?: (newPage: number, itemsPerPage: number) => void
  onSearchDoctor?: (text: string) => void
  open: boolean
  totalDoctors: number
}

const rowsPerPageOptions = [5, 10, 15]
export const defaultAddDoctorFormInitialValues: AddDoctorFormValues = {
  name: '',
  surname: '',
  cps: '',
  email: '',
  phone: '',
  cp: '',
}

export function AddDoctorDialog(props: AddDoctorDialogProps) {
  const { doctors, isAdding = false, isSearching = false, onAddDoctor, onClose, onItemsPerPageChange, onPageChange, onSearchDoctor, open, totalDoctors } = props
  const { t } = useTranslation()

  const [addDoctorFormDialogOpen, setAddDoctorFormDialogOpen] = useState<boolean>(false)
  const [addDoctorFormInitialValues, setAddDoctorFormInitialValues] = useState<AddDoctorFormValues>(defaultAddDoctorFormInitialValues)
  const [itemsPerPage, setItemsPerPage] = useState<number>(rowsPerPageOptions[0])
  const [page, setPage] = useState<number>(1)

  useEffect(() => {
    if (!open) setAddDoctorFormDialogOpen(false)
  }, [open])

  const debouncedOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchDoctor?.(event.target.value)
  }, 300)

  const handleAddDoctor = (values: AddDoctorFormValues) => {
    onAddDoctor?.(values)
  }

  const handleClickAddDoctor = (doctor: DoctorData) => {
    setAddDoctorFormInitialValues({
      name: doctor.name,
      surname: doctor.surname,
      cps: doctor.cps,
      email: doctor.email,
    })
    setAddDoctorFormDialogOpen(true)
  }

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, itemsPerPage)
  }

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    if (itemsPerPage === newItemsPerPage) return
    setPage(1)
    setItemsPerPage(newItemsPerPage)
    onItemsPerPageChange?.(newItemsPerPage)
  }

  const toggleAddDoctorFormDialog = () => {
    if (addDoctorFormDialogOpen) setAddDoctorFormInitialValues(defaultAddDoctorFormInitialValues)
    setAddDoctorFormDialogOpen(!addDoctorFormDialogOpen)
  }

  return (
    <>
      <Dialog onClose={onClose} open={open} PaperProps={{ sx: { maxWidth: (theme) => theme.breakpoints.values.lg, width: '100%' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
          {t('component.group.addDoctorDialog.title')}
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <TextInput
                  defaultValue=""
                  fullWidth
                  onChange={debouncedOnChange}
                  placeholder={t('component.group.addDoctorDialog.searchDoctorInput.placeholder')}
                />
              </Box>
              <Button onClick={toggleAddDoctorFormDialog} variant="contained">
                {t('component.group.addDoctorDialog.addDoctorButton.label')}
              </Button>
            </Stack>
            <TableContainer component={Paper}>
              <AddDoctorTable loading={isSearching} onAdd={handleClickAddDoctor} data={doctors} />
              <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                <Pagination
                  color="primary"
                  itemsPerPageOptions={rowsPerPageOptions}
                  shape="rounded"
                  page={page}
                  onPageChange={handleChangePage}
                  onItemsPerPageChange={handleChangeItemsPerPage}
                  totalItems={totalDoctors}
                />
              </Box>
            </TableContainer>
          </Stack>
        </DialogContent>
      </Dialog>
      <AddDoctorFormDialog
        initialValues={addDoctorFormInitialValues}
        isSubmitting={isAdding}
        onClose={toggleAddDoctorFormDialog}
        onSubmit={handleAddDoctor}
        open={addDoctorFormDialogOpen}
      />
    </>
  )
}

export default AddDoctorDialog
